import { absUrl, OG_IMAGE, OG_IMAGE_ALT, SITE_URL, TITLE, DESCRIPTION } from "@/lib/site";

export const PAGE_SEO = {
  home: {
    title: TITLE,
    description: DESCRIPTION,
    path: "/",
  },
  collection: {
    title: "Handmade Doll Clothes Collection | Doll Clothes by Ella Wise",
    description:
      "Browse handmade 18-inch doll outfits by Ella Wise — see what's in each set, the craft used, sizing and fit, care guidance, and how ordering works in Midland, Michigan.",
    path: "/collection",
  },
  personalization: {
    title: "Custom & Personalized Doll Clothes | Doll Clothes by Ella Wise",
    description:
      "Custom 18-inch doll clothes by Ella Wise: choose colors, themes, matching accessories and occasions. See how a made-to-order piece works, plus custom-order FAQs.",
    path: "/personalization",
  },
  about: {
    title: "About Ella Wise | Handmade Doll Clothes in Midland, MI",
    description:
      "Meet Ella Wise, who knits, crochets and sews every doll outfit herself in Midland, Michigan. Her craft, her approach to made-to-order gifts, and customer reviews.",
    path: "/about",
  },
  contact: {
    title: "Contact Ella Wise | Doll Clothes FAQs & Custom Orders",
    description:
      "Ask Ella Wise about handmade 18-inch doll clothes, custom orders, sizing, or gift help. Call 989-388-3556, email wisella@aol.com, or read the FAQs before you write.",
    path: "/contact",
  },

} as const;

type FetchPriority = "high" | "low" | "auto";

type HeadMeta = {
  title?: string;
  name?: string;
  property?: string;
  content?: string;
};

type HeadLink = {
  rel: string;
  href: string;
  as?: string;
  type?: string;
  title?: string;
  fetchPriority?: FetchPriority;
  imageSrcSet?: string;
  imageSizes?: string;
};

type HeadScript = {
  type?: string;
  src?: string;
  async?: boolean;
  children?: string;
};

type PageHeadOptions = {
  title: string;
  description: string;
  path: string;
  extraLinks?: HeadLink[];
  extraMeta?: HeadMeta[];
  scripts?: HeadScript[];
  preload?: {
    href: string;
    type?: string;
    imageSrcSet?: string;
    imageSizes?: string;
    fetchPriority?: FetchPriority;
  };
};

export function pageHead({
  title,
  description,
  path,
  extraLinks = [],
  extraMeta = [],
  scripts = [],
  preload,
}: PageHeadOptions) {
  const url = absUrl(path);
  const links: HeadLink[] = [{ rel: "canonical", href: url }];

  if (preload) {
    const preloadLink: HeadLink = {
      rel: "preload",
      href: preload.href,
      as: "image",
      type: preload.type ?? "image/avif",
      fetchPriority: preload.fetchPriority ?? "high",
    };
    if (preload.imageSrcSet) preloadLink.imageSrcSet = preload.imageSrcSet;
    if (preload.imageSizes) preloadLink.imageSizes = preload.imageSizes;
    links.push(preloadLink);
  }

  links.push(...extraLinks);

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: OG_IMAGE_ALT },
      { property: "og:image:width", content: "680" },
      { property: "og:image:height", content: "510" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: OG_IMAGE_ALT },
      ...extraMeta,
    ],
    links,
    scripts,
  };
}

export function jsonLdScript(data: unknown): HeadScript {
  return {
    type: "application/ld+json",
    children: JSON.stringify(data),
  };
}

export function webPageGraph({
  title,
  description,
  path,
}: {
  title: string;
  description: string;
  path: string;
}) {
  const url = absUrl(path);
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${SITE_URL}/#business` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: OG_IMAGE,
          caption: OG_IMAGE_ALT,
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: absUrl("/"),
          },
          {
            "@type": "ListItem",
            position: 2,
            name: title,
            item: url,
          },
        ],
      },
    ],
  };
}
